import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconButton } from './ui';
import styles from './ui.module.css';

/**
 * A bottom sheet on a phone, a centred dialog on a desktop.
 *
 * Built on <dialog> so focus trapping, Escape and inertness of the page behind
 * come from the platform instead of from hand-written key handling.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  closeLabel,
}: {
  open: boolean;
  onClose(): void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  // iOS keeps scrolling the page behind a modal dialog; pin it while open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={styles.sheet}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Only a click on the backdrop itself, never one inside the panel.
        if (event.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheetPanel}>
        <div className={styles.sheetHandle} />
        <div className={styles.sheetHead}>
          <h2 id={titleId} className={styles.sheetTitle}>
            {title}
          </h2>
          <IconButton name="close" label={closeLabel} onClick={onClose} />
        </div>
        <div className={styles.sheetBody}>{children}</div>
        {footer && <div className={styles.sheetFooter}>{footer}</div>}
      </div>
    </dialog>
  );
}
